/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DateFormat } from '@gh/core-data';
import { DateTimeType, MaskedTextType, NumberType, QuantityType, TextType, Type } from '@gh/core-type';
import {
  asString,
  coalesce,
  combinePredicatesByAnd,
  identityCollectionComparator,
  valueComparator,
} from '@gh/core-util';
import {
  ColDef,
  IsColumnFunc,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';

import {
  assign,
  constant,
  eq,
  isBoolean,
  isEmpty,
  isFunction,
  isNil,
  isString,
  keyBy,
  property,
  uniqueId,
} from 'lodash';
import { Observable, Subscription } from 'rxjs';

import { gridEditableValueCollectionComparator } from '../components/grid.utils';
import { CellParams, EditableCellAccessor, EditableCellModelFactory, EditableCellOptionSet } from '../state';
import { EditableCellState } from '../state/grid-state.interfaces';
import {
  GridAutocompleteCellConfig,
  GridAutocompleteCellEditorComponent,
  GridDateTimeCellEditorComponent,
  GridMaskedCellEditorComponent,
  GridNumberCellEditorComponent,
  GridQuantityCellEditorComponent,
  GridSelectCellEditorComponent,
  GridTextareaCellEditorComponent,
  GridTextCellEditorComponent,
} from './editors';
import { HeaderWithExternalFilterCallbackParams, HeaderWithExternalFilterComponent } from './headers';
import {
  GridButtonCellRendererComponent,
  GridCheckboxCellRendererComponent,
  GridEditableCellRendererComponent,
  GridRadioCellRendererComponent,
  GridTooltipCellRendererComponent,
  GroupCellRenderer,
  GroupTitleCellRenderer,
} from './renderers';
import {
  GridCellRendererEditorTrigger,
} from './renderers/grid-editable-cell-renderer/grid-editable-cell-renderer.component';
import { GroupCellRendererOptions } from './renderers/group-cell-renderer';

export interface EditableCellValue extends EditableCellState {
  editable: boolean;
}
export interface MenuItem {
  text: string;
  icon: string;
  onClick(entity: any): void;
}
export type GridEditableCellAlignment = 'left' | 'right' | 'center';

export interface GridEditableColumnOptions {
  modelFactory?: EditableCellModelFactory;
  align?: GridEditableCellAlignment;
  editorTrigger?: GridCellRendererEditorTrigger;
  hasContentTooltip?: boolean;
  hasSelectTrigger?: boolean;
  clearable?: boolean;
  forceClearable?: boolean;
  onClear?: ProcedureFn<CellParams>;
  onEditorOpen?: ProcedureFn<CellParams>;
  singleClickEdit?: boolean;
  useEditableAsEnabled?: boolean;
  hasAlternativeText?: TransformFn<CellParams, boolean>;
  getAlternativeText?: TransformFn<CellParams, string>;
  cursorOnFocus?: 'start' | 'end';
  menu?: MenuItem[];
  showMenu?(data: any): boolean;
}

/**
 * Common options for all editable cells
 */
export interface GridCellEditorOptions {
  align?: GridEditableCellAlignment;
  clearable?: boolean;
  forceClearable?: boolean;
  useEditableAsEnabled?: boolean;
  menu?: MenuItem[];
  showMenu?(data: any): boolean;

  hasAlternativeText?(params: CellParams): boolean;

  getAlternativeText?(params: CellParams): string;

}

/**
 * Options specific for textarea editable cell
 */
export interface GridTextareaCellEditorOptions extends GridCellEditorOptions {
  minRows?: number;
  maxRows?: number;
}

export interface GridDateTimeCellEditorOptions extends GridCellEditorOptions {
  format?: DateFormat;
  cursorOnFocus?: 'start' | 'end';
  isDateAllowed?(entity: any, date: Date): boolean;

  isTimeAllowed?(entity: any, date: Date): boolean;
}

export interface GridCheckboxCellOptions {
  useEditableAsEnabled?: boolean;
}

export interface GridRadioCellOptions {
  useEditableAsEnabled?: boolean;

  onChange(params: CellParams, value: boolean): void;
}

export interface GroupTitleRendererOptions {
  getGroupKey: TransformFn<any, any>;
  isGrouped: PredicateFn<any>;
}

export interface GridButtonCellOptions {
  type: 'raised' | 'icon' | 'flat';
  icon?: string;
  onClick: ProcedureFn<CellParams>;
  isClickable?: PredicateFn<CellParams>;
  label: string | TransformFn<CellParams, string>;
}

export interface GridHeaderWithExternalFilterOptions {
  title?: string;
  filtered: Observable<boolean>;

  onFilter(): void;
}

export interface TooltipCellRendererOptions {
  tooltip: string | TransformFn<{ data: any }, string>;
}

const EDITABLE_COLUMN_COMPARATOR = gridEditableValueCollectionComparator(identityCollectionComparator);
const EDITABLE_CELL_VALUE_FORMATTER = (params: any) => params.value.value || '';

export const emptyValueFormatter = constant('');
export const typedValueFormatter = (type: Type<any> | TransformFn<any, Type<any>>) =>
  isFunction(type)
    ? (params: any) => isNil(params.value) ? ' ' : type(params.data).format(params.value)
    : (params: any) => isNil(params.value) ? '' : type.format(params.value);

export function buildColumn(...colDefs: Partial<ColDef>[]): ColDef {
  return assign({}, ...colDefs);
}

export function buildButtonColumn(options: GridButtonCellOptions): Partial<ColDef> {
  return {
    cellRendererFramework: GridButtonCellRendererComponent,
    cellRendererParams: options,
  };
}

export function buildNumberColumn(type: NumberType): Partial<ColDef> {
  return {
    valueFormatter: typedValueFormatter(type),
    cellClass: 'text-right',
  };
}

export function buildNumberColumnLeft(type: NumberType): Partial<ColDef> {
  return {
    valueFormatter: typedValueFormatter(type),
  };
}

export function buildQuantityColumn(type: QuantityType<any> | TransformFn<any, QuantityType<any>>): Partial<ColDef> {
  return {
    valueFormatter: typedValueFormatter(type),
    cellClass: 'text-right',
  };
}

export function buildDateTimeColumn(type: DateTimeType): Partial<ColDef> {
  return {
    valueFormatter: typedValueFormatter(type),
  };
}

export function buildGroupTitleColumn(options: GroupTitleRendererOptions): Partial<ColDef> {
  return {
    cellRenderer: GroupTitleCellRenderer,
    cellRendererParams: options,
  };
}

export function buildGroupColumn(options: GroupCellRendererOptions): Partial<ColDef> {
  return {
    cellRenderer: GroupCellRenderer,
    cellRendererParams: options,
  };
}

export function useTooltipInColumn(options: TooltipCellRendererOptions): Partial<ColDef> {
  return {
    cellRendererFramework: GridTooltipCellRendererComponent,
    cellRendererParams: options,
  };
}

export function buildEditableNumberColumn(type: NumberType | TransformFn<any, NumberType>,
                                          cellAccessor: EditableCellAccessor,
                                          options?: GridCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      align: options && options.align || 'right',
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    cellEditorFramework: GridNumberCellEditorComponent,
    cellEditorParams: { modelFactory, type },
    valueFormatter: editableTypedValueFormatter(type),
  };
}

export function buildEditableDateTimeColumn(type: DateTimeType | TransformFn<any, DateTimeType>,
                                            cellAccessor: EditableCellAccessor,
                                            options?: GridDateTimeCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  const format = options && options.format || DateFormat.DateTime;
  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      align: options && options.align || 'right',
      clearable: coalesce(options && options.clearable, true),
      forceClearable: coalesce(options && options.forceClearable, false),
      singleClickEdit: format !== DateFormat.DateTime,
      hasSelectTrigger: format !== DateFormat.DateTime,
      onClear: (params: CellParams) => {
        const { data, colDef: { field } } = params;
        cellAccessor.setValue(params, field ? data[field] : void 0, void 0);
      },
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    cellEditorFramework: GridDateTimeCellEditorComponent,
    cellEditorParams: {
      modelFactory,
      type,
      format,
      cursorOnFocus: options && options.cursorOnFocus || 'end',
      isDateAllowed: options && options.isDateAllowed,
      isTimeAllowed: options && options.isTimeAllowed,
    },
    valueFormatter: editableTypedValueFormatter(type),
  };
}

export function buildEditableQuantityColumn(type: QuantityType<any> | TransformFn<any, QuantityType<any>>,
                                            cellAccessor: EditableCellAccessor,
                                            options?: GridCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      align: options && options.align || 'right',
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    cellEditorFramework: GridQuantityCellEditorComponent,
    cellEditorParams: { modelFactory, type },
    valueFormatter: editableTypedValueFormatter(type),
  };
}

export function buildEditableMaskedColumn(type: MaskedTextType | TransformFn<any, MaskedTextType>,
                                          cellAccessor: EditableCellAccessor,
                                          options?: GridCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      align: options && options.align,
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    cellEditorFramework: GridMaskedCellEditorComponent,
    cellEditorParams: { modelFactory, type },
    valueFormatter: editableTypedValueFormatter(type),
  };
}

export function buildEditableTextColumn(type: TextType | TransformFn<any, TextType>,
                                        cellAccessor: EditableCellAccessor,
                                        options?: GridCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      menu: options && options?.menu && options.menu.length ? options.menu : undefined,
      showMenu: options && options?.showMenu ? options.showMenu : undefined,
      align: options && options.align,
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    cellEditorFramework: GridTextCellEditorComponent,
    cellEditorParams: { modelFactory, type },
  };
}

export function buildEditableMultilineTextColumn(type: TextType | TransformFn<any, TextType>,
                                                 cellAccessor: EditableCellAccessor,
                                                 options?: GridTextareaCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      align: options && options.align,
      hasContentTooltip: true,
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    cellEditorFramework: GridTextareaCellEditorComponent,
    cellEditorParams: { modelFactory, type, options },
  };
}

export function buildEditableAutocompleteColumn(config: GridAutocompleteCellConfig,
                                                cellAccessor: EditableCellAccessor,
                                                options?: GridCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  const getCellTextValue = (value: any) => {
    const nameProperty = isString(config.nameProperty) ? property(config.nameProperty) : config.nameProperty;
    return asString(value && (config.displayProperty
      ? value[config.displayProperty]
      : nameProperty(value)));
  };

  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      align: options && options.align,
      hasSelectTrigger: true,
      singleClickEdit: true,
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    comparator: gridEditableValueCollectionComparator(valueComparator(getCellTextValue)),
    cellEditorFramework: GridAutocompleteCellEditorComponent,
    cellEditorParams: { modelFactory, config },
    valueFormatter: ({ value: { value } }: { value: EditableCellState }) => getCellTextValue(value),
  };
}

export function buildEditableCheckboxColumn(cellAccessor: EditableCellAccessor,
                                            options?: GridCheckboxCellOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  return {
    editable: false,
    cellRendererFramework: GridCheckboxCellRendererComponent,
    cellRendererParams: {
      modelFactory,
      color: 'primary',
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      onChange: (params: CellParams, value: boolean) => {
        const { data, colDef: { field } } = params;
        cellAccessor.setValue(params, field ? data[field] : void 0, value);
      },
    },
    comparator: EDITABLE_COLUMN_COMPARATOR,
    valueGetter: createEditableCellValueGetter(cellAccessor),
    equals: createEditableCellValueEquals(cellAccessor),
  };
}

export function buildEditableRadioColumn(cellAccessor: EditableCellAccessor,
                                         options: GridRadioCellOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);

  return {
    editable: false,
    cellRendererFramework: GridRadioCellRendererComponent,
    cellRendererParams: {
      modelFactory,
      color: 'primary',
      name: uniqueId('grid-radio:'),
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      onChange: options.onChange,
    },
    comparator: EDITABLE_COLUMN_COMPARATOR,
    valueGetter: createEditableCellValueGetter(cellAccessor),
    equals: createEditableCellValueEquals(cellAccessor),
  };
}

export function buildEditableSelectColumn(optionSet: EditableCellOptionSet,
                                          cellAccessor: EditableCellAccessor,
                                          options?: GridCellEditorOptions): Partial<ColDef> {
  const modelFactory = createCellModelFactory(cellAccessor);
  const cellAccessorEditable = createCellEditablePredicate(cellAccessor);
  const optionSetEditable = optionSet.hasOptions ? createCellEditableOptionSetPredicate(optionSet) : true;
  const align = coalesce(options && options.align, 'left');

  return {
    ...buildEditableColumn(cellAccessor, {
      modelFactory,
      align,
      useEditableAsEnabled: coalesce(options && options.useEditableAsEnabled, false),
      hasSelectTrigger: true,
      singleClickEdit: true,
      clearable: coalesce(options && options.clearable, true),
      forceClearable: coalesce(options && options.forceClearable, false),
      onClear: (params: CellParams) => {
        const { data, colDef: { field } } = params;
        cellAccessor.setValue(params, field ? data[field] : void 0, void 0);
      },
      hasAlternativeText: options && options.hasAlternativeText,
      getAlternativeText: options && options.getAlternativeText,
    }),
    editable: toIsColumnPredicate(combinePredicatesByAnd(isRegularRow, cellAccessorEditable, optionSetEditable)),
    cellEditorFramework: GridSelectCellEditorComponent,
    cellEditorParams: { modelFactory, optionSet, align },
    comparator: createEditableCellSelectComparator(optionSet),
    valueFormatter: createEditableCellOptionFormatter(cellAccessor, optionSet),
  };
}

export function buildEditableColumn(cellAccessor: EditableCellAccessor,
                                    options: GridEditableColumnOptions): Partial<ColDef> {
  const modelFactory = options.modelFactory || createCellModelFactory(cellAccessor);
  const cellAccessorEditable = createCellEditablePredicate(cellAccessor);

  return {
    editable: toIsColumnPredicate(combinePredicatesByAnd(isRegularRow, cellAccessorEditable)),
    cellRendererFramework: GridEditableCellRendererComponent,
    cellRendererParams: {
      modelFactory,
      ...options,
    },
    valueSetter: createCellValueSetter(cellAccessor),
    valueGetter: createEditableCellValueGetter(cellAccessor),
    equals: createEditableCellValueEquals(cellAccessor),
    comparator: EDITABLE_COLUMN_COMPARATOR,
    valueFormatter: EDITABLE_CELL_VALUE_FORMATTER,
  };
}

export function buildOptionSetFromObject(object: Hash<any>): EditableCellOptionSet {
  const optionSet = Object.keys(object).map((key) => ({ id: key, name: object[key] }));
  return {
    getOptions: constant(optionSet),
    hasOptions: constant(!isEmpty(object)),
    format: ({ data, colDef: { field } }) => field ? object[data[field]] : '',
  };
}

export function buildOptionSetFromArray(options: any[],
                                        idProperty: string = 'id',
                                        nameProperty: string = 'name'): EditableCellOptionSet {
  const index = keyBy(options, idProperty);
  return {
    getOptions: constant(options.map((option) => ({ id: option[idProperty], name: option[nameProperty] }))),
    hasOptions: constant(!isEmpty(options)),
    format: ({ data, colDef: { field } }) => {
      const entry = field ? index[data[field]] : void 0;
      return entry ? entry[nameProperty] : '';
    },
  };
}

export function buildHeaderWithExternalFilter(options: GridHeaderWithExternalFilterOptions): Partial<ColDef> {
  let subscription: Maybe<Subscription>;

  return {
    headerComponent: HeaderWithExternalFilterComponent,
    headerComponentParams: {
      title: options.title,
      onInit: ({ setFiltered }: HeaderWithExternalFilterCallbackParams) => {
        subscription = options.filtered.subscribe(setFiltered);
      },
      onDestroy: () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      },
      onFilter: options.onFilter,
    },
  };
}

export function editableTypedValueFormatter(type: Type<any> | TransformFn<any, Type<any>>): (params: any) => string {
  return isFunction(type)
    ? (params) => isNil(params.value) || isNil(params.value.value) ? ' ' : type(params.data).format(params.value.value)
    : (params) => isNil(params.value) || isNil(params.value.value) ? ' ' : type.format(params.value.value);
}

function createCellEditablePredicate(cellAccessor: EditableCellAccessor): PredicateFn<CellParams> | boolean {
  const { isEditable } = cellAccessor;

  return isEditable ? isEditable : true;
}

function createCellEditableOptionSetPredicate(optionSet: EditableCellOptionSet): PredicateFn<CellParams> | boolean {
  // tslint:disable-next-line:no-non-null-assertion
  return optionSet.hasOptions ? (params: CellParams) => optionSet.hasOptions!(params) : true;
}

function toIsColumnPredicate(predicate: PredicateFn<CellParams> | boolean): IsColumnFunc | boolean {
  return isBoolean(predicate) ? predicate : ({ node, colDef }) => predicate({ node, colDef, data: node.data });
}

export function createCellModelFactory(cellAccessor: EditableCellAccessor): EditableCellModelFactory {
  return (params) => {
    const state = cellAccessor.getState(params);

    const required = cellAccessor.isRequired ? cellAccessor.isRequired(params) : false;
    const editable = cellAccessor.isEditable ? cellAccessor.isEditable(params) : false;
    const validate = cellAccessor.validate ?
      (value: any) => cellAccessor.validate && cellAccessor.validate(params, value) : constant(void 0);

    return {
      required,
      editable,
      validate,
      valid: isNil(state.errors),
      invalid: !isNil(state.errors),
      value: cellAccessor.getValue(state),
      errors: state.errors,
    };
  };
}

function createCellValueSetter(cellAccessor: EditableCellAccessor): (params: ValueSetterParams) => boolean {
  return (params: ValueSetterParams) => {
    cellAccessor.setValue(
      {
        node: params.node,
        data: params.data,
        colDef: params.colDef,
      },
      params.oldValue,
      params.newValue);

    return true;
  };
}

function createEditableCellValueGetter(cellAccessor: EditableCellAccessor): (params: ValueGetterParams) => any {
  return ({ colDef, node, data }: ValueGetterParams) => {
    const params = { colDef, node, data };
    const editable = cellAccessor.isEditable ? cellAccessor.isEditable(params) : true;
    return { ...cellAccessor.getState(params), editable };
  };
}

function createEditableCellOptionFormatter(cellAccessor: EditableCellAccessor,
                                           optionSet: EditableCellOptionSet): (params: ValueFormatterParams) => string {
  return ({ node, colDef, data }) => {
    const params = { node, colDef, data };
    return optionSet.format(params, cellAccessor.getState(params).value);
  };
}

function createEditableCellValueEquals(accessor: EditableCellAccessor): (a: any, b: any) => boolean {
  const valueEq = accessor.equals ? accessor.equals : eq;

  return (oldState: EditableCellValue, newState: EditableCellValue) =>
    valueEq(oldState.value, newState.value)
    && eq(oldState.errors, newState.errors)
    && eq(oldState.editable, newState.editable);
}

function createEditableCellSelectComparator(optionSet: EditableCellOptionSet): (valueLeft: any,
                                                                                valueRight: any,
                                                                                nodeLeft: RowNode,
                                                                                nodeRight: RowNode) => number {
  return (leftValue: any, rightValue: any, nodeLeft: RowNode, nodeRight: RowNode) =>
    // tslint:disable-next-line:no-non-null-assertion
    identityCollectionComparator(
      optionSet.format(<CellParams>{ node: nodeLeft, data: nodeLeft.data }, leftValue.value),
      optionSet.format(<CellParams>{ node: nodeRight, data: nodeRight.data }, rightValue.value));
}

function isRegularRow({ node }: CellParams): boolean {
  return !node.isRowPinned();
}
